import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHero, Section, Btn } from "@/components/kit";
import { Eyebrow } from "@/lib/anim";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Foreign currency per 1 AUD (use your bank's rate, not mid-market).
const FX = { USD: 0.65, CNY: 4.7, INR: 55, EUR: 0.6, TRY: 21, BDT: 78, VND: 16500, AED: 2.4, AUD: 1 };

const n = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));
const money = (v, dp = 2) => `$${(Math.round(v * 10 ** dp) / 10 ** dp).toLocaleString("en-AU", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const pct = (v) => `${(Math.round(v * 1000) / 10).toLocaleString("en-AU")}%`;

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block t-caption font-mono uppercase tracking-wide text-[var(--muted)] leading-snug min-h-[2.4em]">{label}</span>
      {children}
      {hint && <span className="block t-caption text-[var(--muted)] mt-1 leading-snug">{hint}</span>}
    </label>
  );
}

function verdict(m) {
  if (m >= 0.65) return ["That is a healthy intake margin. Check that quality and quantity assumptions are realistic before you rely on it.", "var(--olive)"];
  if (m >= 0.55) return ["Workable for most brands, with room for a normal discount cycle.", "var(--olive)"];
  if (m >= 0.45) return ["Tight. A 20% average discount will take this below a sustainable level.", "var(--clay-deep)"];
  if (m >= 0.30) return ["This will not survive markdown, returns and freight variation. The cost, the price or the range needs to change.", "#a24f3a"];
  return ["At this margin the order is unlikely to fund the next one. Book a Margin Check before you commit.", "#a24f3a"];
}

export default function Calculator() {
  const [f, setF] = useState({
    price: 12, currency: "USD", fx: 0.65, units: 300,
    freight: 540, insurance: 0.5, duty: 5, other: 0,
    sampling: 900, amortise: true, bank: 1.5,
    rrp: 79, discount: 15, gst: true, minMargin: 45,
  });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setCur = (c) => setF((p) => ({ ...p, currency: c, fx: FX[c] }));

  const r = useMemo(() => {
    const fx = n(f.fx) || 1;
    const units = Math.max(1, n(f.units));
    const fob = n(f.price) / fx;
    const goods = fob * units;
    const freightU = n(f.freight) / units;
    const insU = (n(f.insurance) / 100) * fob;
    const dutyU = (n(f.duty) / 100) * fob;
    const voti = fob + dutyU + freightU + insU;
    const gstU = 0.1 * voti;
    const otherU = n(f.other) / units;
    const oneoffU = f.amortise ? n(f.sampling) / units : 0;
    const bankU = (n(f.bank) / 100) * fob;
    const landed = fob + freightU + insU + dutyU + otherU + oneoffU + bankU + (f.gst ? 0 : gstU);
    const totalLanded = landed * units;
    const rrpEx = n(f.rrp) / 1.1;
    const intake = rrpEx > 0 ? (rrpEx - landed) / rrpEx : 0;
    const markup = landed > 0 ? n(f.rrp) / landed : 0;
    const netEx = rrpEx * (1 - n(f.discount) / 100);
    const achieved = netEx > 0 ? (netEx - landed) / netEx : 0;
    const contrib = rrpEx - (landed - oneoffU);
    const beUnits = contrib > 0 ? Math.ceil(n(f.sampling) / contrib) : 0;
    const floorEx = landed / (1 - n(f.minMargin) / 100);
    const headroom = rrpEx > 0 ? 1 - floorEx / rrpEx : 0;
    const beDiscount = rrpEx > 0 ? 1 - landed / rrpEx : 0;
    const cash = goods + n(f.freight) + n(f.other) + n(f.sampling) + (f.gst ? 0 : gstU * units);
    const segs = [
      ["Factory (FOB)", fob], ["Freight", freightU], ["Duty", dutyU], ["Insurance", insU],
      ["Other", otherU], ["One-off", oneoffU], ["Bank fees", bankU],
    ];
    if (!f.gst) segs.push(["Import GST", gstU]);
    return { fob, landed, totalLanded, rrpEx, intake, markup, achieved, beUnits, headroom, beDiscount, cash, gstU, segs: segs.filter((s) => s[1] > 0) };
  }, [f]);

  const [vText, vColor] = verdict(r.intake);
  const shades = ["var(--clay)", "var(--stone)", "var(--clay-deep)", "var(--muted-dark)", "var(--sand)", "var(--muted)", "var(--olive)", "var(--charcoal)"];

  const emailResults = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Add your email to receive the results."); return; }
    setSending(true);
    const rows = [
      { label: "Landed cost per unit", value: money(r.landed) },
      { label: "Intake margin", value: pct(r.intake) },
      { label: "Markup multiple", value: `${(Math.round(r.markup * 100) / 100).toFixed(2)}x` },
      { label: `Achieved margin at ${f.discount}% discount`, value: pct(r.achieved) },
      { label: "Markdown headroom", value: pct(r.headroom) },
      { label: "Discount to break-even", value: pct(r.beDiscount) },
      { label: "Break-even units on one-off costs", value: `${r.beUnits}` },
      { label: "Total landed cost of this order", value: money(r.totalLanded, 0) },
      { label: "Cash needed to fund this order", value: money(r.cash, 0) },
      { label: "GST payable at the border", value: `${money(r.gstU * Math.max(1, n(f.units)), 0)}${f.gst ? " (claimable)" : ""}` },
    ];
    try {
      await axios.post(`${API}/calculator/email`, { email: email.trim(), name: name.trim(), rows });
      track("calculator_email", { intake_margin: Math.round(r.intake * 100) });
      toast.success("Sent. Check your inbox.");
      setEmail(""); setName("");
    } catch { toast.error("Could not send. Please try again."); }
    setSending(false);
  };

  const grp = "text-[var(--muted)] font-mono text-[11px] uppercase tracking-widest border-b border-[var(--stone)] pb-2 mb-4";

  return (
    <>
      <Seo title="Landed Cost & Margin Calculator (Australia, GST-aware)" canonical="/tools/landed-cost-and-margin-calculator" description="Free, GST-aware landed cost and margin calculator for Australian brands: factory price in, landed cost, intake margin, break-even quantity, markdown headroom and cash out. No sign-up." keywords="landed cost calculator, landed cost calculation, landed cost Australia, margin calculator, gross margin, intake margin, garment costing, product costing Australia" />
      <PageHero
        eyebrow="Free tools"
        crumbs={[{ label: "Home", to: "/" }, { label: "Tools", to: "/tools" }, { label: "Calculator" }]}
        title="Landed Cost & Margin Calculator"
        lead="Australian dollars. GST-aware. No sign-up to use it. Factory price in, landed cost, intake margin, break-even quantity and markdown headroom out."
      />
      <Section bg="bone">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Inputs */}
          <div className="lg:col-span-7" data-testid="calc-inputs">
            <p className={grp}>The product</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Factory price per unit"><input type="number" step="0.01" className="field mt-2" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="calc-price" /></Field>
              <Field label="Currency"><select className="field mt-2" value={f.currency} onChange={(e) => setCur(e.target.value)} data-testid="calc-currency">{Object.keys(FX).map((c) => <option key={c}>{c}</option>)}</select></Field>
              <Field label={`Exchange rate (${f.currency} per 1 AUD)`} hint="Use your bank's rate, not the mid-market rate"><input type="number" step="0.0001" className="field mt-2" value={f.fx} onChange={(e) => set("fx", e.target.value)} data-testid="calc-fx" /></Field>
              <Field label="Units in this order"><input type="number" className="field mt-2" value={f.units} onChange={(e) => set("units", e.target.value)} data-testid="calc-units" /></Field>
            </div>

            <p className={`${grp} mt-10`}>Getting it here</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Total freight to your door (AUD)"><input type="number" className="field mt-2" value={f.freight} onChange={(e) => set("freight", e.target.value)} /></Field>
              <Field label="Insurance %" hint="of goods value"><input type="number" step="0.1" className="field mt-2" value={f.insurance} onChange={(e) => set("insurance", e.target.value)} /></Field>
              <Field label="Customs duty %" hint="Most apparel is 5%. Confirm with a customs broker."><input type="number" step="0.1" className="field mt-2" value={f.duty} onChange={(e) => set("duty", e.target.value)} /></Field>
              <Field label="Other import charges (AUD, total)" hint="Clearance, port fees, local delivery"><input type="number" className="field mt-2" value={f.other} onChange={(e) => set("other", e.target.value)} /></Field>
            </div>

            <p className={`${grp} mt-10`}>One-off costs</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Sampling, tooling and setup (AUD, total)"><input type="number" className="field mt-2" value={f.sampling} onChange={(e) => set("sampling", e.target.value)} /></Field>
              <Field label="Bank and payment fees %" hint="of goods value"><input type="number" step="0.1" className="field mt-2" value={f.bank} onChange={(e) => set("bank", e.target.value)} /></Field>
              <label className="flex items-center gap-3 sm:col-span-2"><input type="checkbox" checked={f.amortise} onChange={(e) => set("amortise", e.target.checked)} className="w-4 h-4 accent-[var(--clay)]" /><span className="t-sm">Amortise one-off costs into the unit cost</span></label>
            </div>

            <p className={`${grp} mt-10`}>Selling it</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Planned retail price inc GST (AUD)"><input type="number" className="field mt-2" value={f.rrp} onChange={(e) => set("rrp", e.target.value)} data-testid="calc-rrp" /></Field>
              <Field label="Expected average discount %"><input type="number" className="field mt-2" value={f.discount} onChange={(e) => set("discount", e.target.value)} /></Field>
              <Field label="Your minimum acceptable margin %" hint="Used for markdown headroom"><input type="number" className="field mt-2" value={f.minMargin} onChange={(e) => set("minMargin", e.target.value)} /></Field>
              <label className="flex items-center gap-3 self-end pb-3"><input type="checkbox" checked={f.gst} onChange={(e) => set("gst", e.target.checked)} className="w-4 h-4 accent-[var(--clay)]" data-testid="calc-gst" /><span className="t-sm">Registered for GST (border GST claimable)</span></label>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-5 lg:sticky lg:top-24" data-testid="calc-results">
            <div className="card p-8" style={{ background: "var(--charcoal)" }}>
              <Eyebrow>Result, per unit, in AUD</Eyebrow>
              <div className="grid grid-cols-2 gap-6 mt-3">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-wide text-[var(--muted-dark)]">Landed cost</p>
                  <p className="t-stat text-[var(--bone)]" style={{ fontSize: "clamp(30px,3.4vw,42px)" }} data-testid="result-landed">{money(r.landed)}</p>
                </div>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-wide text-[var(--muted-dark)]">Intake margin</p>
                  <p className="t-stat" style={{ fontSize: "clamp(30px,3.4vw,42px)", color: vColor === "#a24f3a" ? "#e0a48f" : "var(--bone)" }} data-testid="result-intake">{pct(r.intake)}</p>
                </div>
              </div>
              <p className="t-caption mt-3" style={{ color: "var(--muted-dark)" }} data-testid="result-verdict">{vText}</p>

              {/* breakdown bar */}
              <div className="mt-6">
                <div className="flex h-3 overflow-hidden" style={{ borderRadius: 2 }}>
                  {r.segs.map((s, i) => <div key={s[0]} title={`${s[0]} ${money(s[1])}`} style={{ width: `${(s[1] / r.landed) * 100}%`, background: shades[i % shades.length] }} />)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-5 mt-7 pt-7 border-t border-white/12">
                {[
                  ["Markup multiple", `${(Math.round(r.markup * 100) / 100).toFixed(2)}x`],
                  [`Achieved at ${f.discount}%`, pct(r.achieved)],
                  ["Markdown headroom", pct(r.headroom)],
                  ["Discount to break-even", pct(r.beDiscount)],
                  ["Break-even units", `${r.beUnits}`],
                  ["GST at border", money(r.gstU * Math.max(1, n(f.units)), 0)],
                ].map(([l, v]) => (
                  <div key={l}><p className="font-mono text-[11px] uppercase tracking-wide text-[var(--muted-dark)]">{l}</p><p className="text-xl font-mono text-[var(--bone)] mt-1">{v}</p></div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-6 mt-5 pt-5 border-t border-white/12">
                <div><p className="font-mono text-[11px] uppercase tracking-wide text-[var(--clay)]">Total landed order</p><p className="text-xl font-mono text-[var(--bone)] mt-1" data-testid="result-total">{money(r.totalLanded, 0)}</p></div>
                <div><p className="font-mono text-[11px] uppercase tracking-wide text-[var(--clay)]">Cash to fund it</p><p className="text-xl font-mono text-[var(--bone)] mt-1" data-testid="result-cash">{money(r.cash, 0)}</p></div>
              </div>
            </div>

            <form onSubmit={emailResults} className="card p-7 mt-5" data-testid="calc-email-form">
              <p className="t-sm font-medium">Email me these results</p>
              <input className="field mt-3" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
              <input type="email" className="field mt-3" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="calc-email" />
              <Btn type="submit" variant="clay" className="w-full justify-center mt-4" data-testid="calc-email-send">{sending ? "Sending\u2026" : "Send me the results"}</Btn>
            </form>
            <p className="t-caption text-[var(--muted)] mt-4">General information, not tax or customs advice. Confirm duty and GST with a customs broker.</p>
          </div>
        </div>
      </Section>

      <Section bg="charcoal" dark className="py-20 md:py-24">
        <div className="max-w-3xl">
          <h2 className="t-display2">Want these numbers checked properly?</h2>
          <p className="t-lead mt-6 text-[var(--stone)]">The Margin Check reviews your real costing sheet, factory quotes and sales data. AUD $690 inc GST.</p>
          <Btn to="/services/costing-pricing-and-margin" variant="clay" className="mt-9">Book the Margin Check, $690</Btn>
        </div>
      </Section>
    </>
  );
}
